import React, { useEffect, useState } from 'react';
import { Button, Box, Typography, TextField } from '@mui/material';
import { lineHeight, styled } from '@mui/system';
import FormErrors from '../../components/FormErrors';
import { changePassword } from '../../lib/allauth';
import { Navigate } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import PopoverCard from '../../components/PopoverCard';
import BackgroundContainer from '../../components/BackgroundContainer';
import BaseHeader from '../../components/BaseHeader';
import BaseButton from '../../components/BaseButton';
import BaseTextField from '../../components/BaseTextField';
import BaseFormContainer from '../../components/BaseFormContainer';

const Subtitle = styled(Typography)({
  fontFamily: 'Playfair Display, serif',
  fontSize: '1.2rem', // 🎯 Full size title
    '@media (max-width: 360px)': {
      fontSize: '1.1rem', // 🧠 Gentle scale-down on ultra-small screens
    },
    '@media (max-width: 320px)': {
      fontSize: '1rem', // 🔒 Last-resort fallback for smallest devices
    },
    textAlign: 'center', // (optional) center if you want

  color: 'rgba(233, 197, 231, 0.7)',
  textAlign: 'center',
  textShadow: '0px 2px 6px rgba(0, 0, 0, 0.5)',
  lineHeight: '1.4',
  marginBottom: '10px',
});


export default function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [newPassword2Errors, setNewPassword2Errors] = useState([]);
  const navigate = useNavigate();
  const [response, setResponse] = useState({ fetching: false, content: null });

  // Popover state
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverMessage, setPopoverMessage] = useState('');

  function submit() {
    if (newPassword !== newPassword2) {
      setNewPassword2Errors([
        { param: 'new_password2', message: 'Password does not match.' },
      ]);
      return;
    }
    setNewPassword2Errors([]);
    setResponse({ ...response, fetching: true });
    changePassword({
      current_password: currentPassword,
      new_password: newPassword,
    })
      .then((resp) => {
        setResponse((r) => {
          return { ...r, content: resp };
        });
      })
      .catch((e) => {
        console.error(e);
        // Instead of window.alert(e), show the popover
        setPopoverMessage(String(e));
        setPopoverOpen(true);
      })
      .then(() => {
        setResponse((r) => {
          return { ...r, fetching: false };
        });
      });
  }

  if (response.content?.status === 200) {
    return <Navigate to="/" />;
  }

  const isChangeButtonDisabled = () => {
    return currentPassword === '' || newPassword === '' || newPassword2 === '';
  };

  return (
    <BackgroundContainer >
      <BaseFormContainer sx={{
    width: '100%',
    padding: { xs: 2, sm: 3 },
    boxSizing: 'border-box',
    marginBottom: '20px',
    '@media (max-height: 780px)' : {
        height : '100%'
    },
  }} >
      <BaseHeader sx={{
    fontSize: '3.5rem',
    '@media (max-width: 380px)': {
      fontSize: '3rem',
    },
    '@media (max-width: 320px)': {
      fontSize: '2.5rem',
    },
    paddingTop: '10px',
    marginBottom: '0.5rem',
    textAlign: 'center',
  }}>Change Password</BaseHeader>
      <Subtitle>
        Enter your current password, followed by your new password
      </Subtitle>
      <Box display="flex" flexDirection="column" alignItems="center">
        <FormErrors param="current_password" errors={response.content?.errors} />
        <BaseTextField
        sx={{
          width: '100%',
          maxWidth: '250px', // 📏 Looks good on larger screens
          fontSize: '1.1rem',
          '& .MuiInputBase-input': {
            padding: '12px',
            fontSize: '1.1rem',
            '@media (max-width: 360px)': {
              padding: '10px',
              fontSize: '1rem', // 🧠 Slightly smaller on small phones
            },
            '@media (max-width: 320px)': {
              padding: '8px',
              fontSize: '0.95rem', // 🧬 Tighter on very narrow phones
            },
          },
        }}
          label="Current Password"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
        />

        <FormErrors param="new_password" errors={response.content?.errors} />
        <BaseTextField
        sx={{
          width: '100%',
          maxWidth: '250px', // 📏 Looks good on larger screens
          fontSize: '1.1rem',
          '& .MuiInputBase-input': {
            padding: '12px',
            fontSize: '1.1rem',
            '@media (max-width: 360px)': {
              padding: '10px',
              fontSize: '1rem', // 🧠 Slightly smaller on small phones
            },
            '@media (max-width: 320px)': {
              padding: '8px',
              fontSize: '0.95rem', // 🧬 Tighter on very narrow phones
            },
          },
        }}
          label="New Password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
        />

        <FormErrors param="new_password2" errors={newPassword2Errors} />
        <BaseTextField
        sx={{
          width: '100%',
          maxWidth: '250px', // 📏 Looks good on larger screens
          fontSize: '1.1rem',
          '& .MuiInputBase-input': {
            padding: '12px',
            fontSize: '1.1rem',
            '@media (max-width: 360px)': {
              padding: '10px',
              fontSize: '1rem', // 🧠 Slightly smaller on small phones
            },
            '@media (max-width: 320px)': {
              padding: '8px',
              fontSize: '0.95rem', // 🧬 Tighter on very narrow phones
            },
          },
        }}
          label="Confirm New Password"
          type="password"
          value={newPassword2}
          onChange={(e) => setNewPassword2(e.target.value)}
          required
        />
<BaseButton
  disabled={isChangeButtonDisabled()}
  onClick={submit}
  sx={{ whiteSpace: 'nowrap', padding: '13px 44px' }}
>
  Change Password
</BaseButton>

<BaseButton
  onClick={() => navigate(-1)}
  sx={{
    fontSize: '1.1rem',
    padding: '10px 24px',
    '@media (max-width: 360px)': {
      fontSize: '1rem',
      padding: '8px 20px',
    },
    '@media (max-width: 320px)': {
      fontSize: '0.95rem',
      padding: '6px 18px',
    },
  }}
>
  Back
</BaseButton>
      </Box>

      {/* Render the PopoverCard to show errors */}
      <PopoverCard
        mainText={popoverMessage}
        open={popoverOpen}
        onClose={() => setPopoverOpen(false)}
      />
      </BaseFormContainer>
    </BackgroundContainer>
  );
}
